import { Button, Icon, Text } from "@ttc3k/trekker";
import { WarningWindow, Xmark } from "iconoir-react";
import { Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Collapsible } from "shared/components/molecules";
import { css } from "styled-system/css";
import { useMediaQuery } from "react-responsive";
import { useLoggedInAs } from "shared/hooks";
import { EnumOwnerType } from "gql/generated";
import { useLocation } from "react-router";
import { useStorefrontView } from "apps/storefront/StorefrontViewContext";

const MOBILE_WIDTH_THRESHOLD = 920;

export const ScreenSizeWarning = () => {
  const { t } = useTranslation("core");
  const loggedInAs = useLoggedInAs();
  const location = useLocation();
  const { storefrontId: contextStorefrontId } = useStorefrontView();
  const isSmallScreen = useMediaQuery({ maxWidth: MOBILE_WIDTH_THRESHOLD });
  const [isDismissed, setIsDismissed] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (isDismissed) return;

    const isInStorefrontView =
      loggedInAs?.type === EnumOwnerType.Storefront || !!contextStorefrontId;
    const isPageNotMobileFriendly = isInStorefrontView
      ? true
      : loggedInAs &&
          loggedInAs.type === EnumOwnerType.Organization &&
          !location.pathname.includes("/home")
        ? true
        : false;

    setShowWarning(isSmallScreen && isPageNotMobileFriendly ? true : false);
  }, [isDismissed, isSmallScreen, loggedInAs, location, contextStorefrontId]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setShowWarning(false);
  };

  const handleCloseForever = () => {
    localStorage.setItem("screenSizeWarningDismissed", "true");
    setIsDismissed(true);
    setShowWarning(false);
  };

  // Read the user's local storage to see if they have dismissed this warning forever
  useEffect(() => {
    const isDismissedForever =
      localStorage.getItem("screenSizeWarningDismissed") === "true";
    if (isDismissedForever) {
      setIsDismissed(true);
      setShowWarning(false);
    }
  }, []);

  return (
    <Collapsible open={showWarning}>
      <Flex
        w={"full"}
        px="300"
        py="200"
        bg={
          "linear-gradient(90deg, token(colors.blue.800) 0%, token(colors.blue.600) 100%)"
        }
        justifyContent={"flex-start"}
        alignItems={"flex-start"}
        gap="200"
      >
        <Flex gap="150">
          <Icon
            Element={WarningWindow}
            color="white"
            w="24px"
            h="24px"
            flexShrink={0}
          />
          <Flex flexDir="column" gap="100">
            <Text visual="smallMedium" color="white">
              {t("SCREEN_SIZE_WARNING.MESSAGE")}
            </Text>
            <Button
              visual="unstyled"
              color="white"
              opacity={{
                base: 0.6,
                _hover: 0.9,
                _focusVisible: 0.9,
                _active: 0.9,
              }}
              textDecoration={{
                base: "none",
                _hover: "underline",
                _focusVisible: "underline",
                _active: "underline",
              }}
              _focusVisible={{
                outlineWidth: "0px",
              }}
              transition="opacity 0.2s ease-in-out"
              onClick={handleCloseForever}
              width="fit-content"
            >
              {t("SCREEN_SIZE_WARNING.CTA_NEVER_AGAIN")}
            </Button>
          </Flex>
        </Flex>
        <Button
          visual="unstyled"
          color="white"
          className={css({
            _hover: { color: "token(colors.ink.400)" },
            _focusVisible: { color: "token(colors.ink.400)" },
            _active: { color: "token(colors.ink.500)" },
          })}
          transition="color 0.2s ease-in-out"
          onClick={handleDismiss}
        >
          <Icon Element={Xmark} w="24px" h="24px" />
        </Button>
      </Flex>
    </Collapsible>
  );
};
